import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useApiData } from '../hooks/useApiData';
import {
  ClientCell,
  EmptyState,
  ErrorState,
  FlagsCell,
  LoadingState,
  MetricBar,
  ScoreCell,
  StatusPill
} from '../components/primitives';
import RangeControl, { DEFAULT_RANGE, rangeLabel } from '../components/RangeControl';
import { clientPath } from '../lib/rangeParams';
import { codeOf, fmtInt, fmtMoney } from '../lib/format';
import { ClientRow, TimeRange } from '../types/superadmin';

type SortKey =
  | 'name'
  | 'sessions'
  | 'minutes'
  | 'callQualityScore'
  | 'openTickets'
  | 'mrr'
  | 'riskScore';

// 'bar' columns render a 0-100 bar; 'score' render a compact number; the rest
// are plain numbers/money. The overall Call Quality score is the rep-pooled
// Lushin rubric percentage. (Per-dimension breakdowns live on the client detail
// page + the Reps page; the former live-intelligence dims Obj/FAQ, Landmine,
// Competitor and the Training column are commented out for Lushin.)
type ColKind = 'score' | 'bar' | 'int' | 'minutes' | 'money';
const COLUMNS: Array<{ key: SortKey; label: string; kind: ColKind }> = [
  { key: 'sessions', label: 'Sessions', kind: 'int' },
  { key: 'minutes', label: 'Minutes', kind: 'minutes' },
  { key: 'callQualityScore', label: 'Call quality', kind: 'bar' },
  { key: 'openTickets', label: 'Tickets', kind: 'int' },
  { key: 'mrr', label: 'MRR', kind: 'money' }
];

// Fixed Client col + metric COLUMNS + Plan, Seats, Risk. (KB dropped: it
// counted live-intelligence components this fork never populates.)
const TOTAL_COLS = 1 + COLUMNS.length + 3;

// Sort accessor; null scores sort last regardless of direction.
const sortVal = (r: ClientRow, key: SortKey): number | null => {
  switch (key) {
    case 'sessions':
      return r.activity.sessions;
    case 'minutes':
      return r.activity.minutes;
    case 'callQualityScore':
      // Overall Lushin rubric call-quality (rep-pooled), from range activity.
      return r.activity.callQualityScore;
    case 'openTickets':
      return r.openTickets;
    case 'mrr':
      return r.mrr;
    case 'riskScore':
      return r.riskScore;
    default:
      return 0;
  }
};

// Render one metric cell by its column kind.
function MetricCell({ row, col }: { row: ClientRow; col: { key: SortKey; kind: ColKind } }) {
  const v = sortVal(row, col.key);
  switch (col.kind) {
    case 'score':
      return <ScoreCell value={v} />;
    case 'bar':
      return <MetricBar value={v} />;
    case 'minutes':
      return (
        <span className="metric-num">
          {fmtInt(v || 0)}
          <span className="u">min</span>
        </span>
      );
    case 'money':
      return <span className="metric-num">{fmtMoney(v || 0)}</span>;
    case 'int':
    default:
      return (
        <span className={'metric-num' + (col.key === 'openTickets' && (v || 0) > 0 ? ' warn' : '')}>
          {fmtInt(v || 0)}
        </span>
      );
  }
}

export default function Clients() {
  const [range, setRange] = useState<TimeRange>(DEFAULT_RANGE);
  const { data, loading, error } = useApiData(() => api.getClients(range), [JSON.stringify(range)]);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  // Internal testing tenants (dev/demo/apexia) are hidden by default.
  const [showInternal, setShowInternal] = useState(false);
  // Default ordering is by risk profile (most flags first); the Client column
  // header toggles alphabetical-by-name ordering.
  const [sortKey, setSortKey] = useState<SortKey>('riskScore');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const rows = useMemo(() => data?.data.clients ?? [], [data]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (!showInternal && r.isInternal) return false;
      if (atRiskOnly && !r.isAtRisk) return false;
      if (!term) return true;
      return r.displayName.toLowerCase().includes(term) || r.tenantId.toLowerCase().includes(term);
    });
    return [...filtered].sort((a, b) => {
      // Name sorts alphabetically; every other column sorts numerically with
      // null scores pushed to the bottom regardless of direction.
      if (sortKey === 'name') {
        const cmp = a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });
        return sortDir === 'desc' ? -cmp : cmp;
      }
      const av = sortVal(a, sortKey);
      const bv = sortVal(b, sortKey);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [rows, search, atRiskOnly, showInternal, sortKey, sortDir]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  const setSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    else {
      setSortKey(k);
      // Name reads best A→Z first; numeric columns read best high-to-low first.
      setSortDir(k === 'name' ? 'asc' : 'desc');
    }
  };
  const arrow = (k: SortKey) => (sortKey === k ? (sortDir === 'desc' ? '↓' : '↑') : '↕');
  const thCls = (k: SortKey) => 'sortable' + (sortKey === k ? ' sorted' : '');
  const consideredRows = showInternal ? rows : rows.filter((r) => !r.isInternal);
  const atRiskCount = consideredRows.filter((r) => r.isAtRisk).length;
  const internalCount = rows.filter((r) => r.isInternal).length;

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Clients · {rangeLabel(range)}</div>
          <h1 className="title">
            <span className="serif">Every client, one table</span>
          </h1>
          <p className="subtitle">
            {rows.length} clients · {atRiskCount} at risk. Score columns are the average across the
            client's reps for the selected period; plan, seats and risk are current. Click a row
            to drill in.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <RangeControl value={range} onChange={setRange} />
        </div>
      </div>

      <div className="filter-bar">
        <span className="label">Search</span>
        <input
          className="search-input"
          placeholder="Client name or id…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="sep" />
        <button className={'filter-chip' + (atRiskOnly ? ' on' : '')} onClick={() => setAtRiskOnly((v) => !v)}>
          At risk only<span className="c">{atRiskCount}</span>
        </button>
        {internalCount > 0 && (
          <button
            className={'filter-chip' + (showInternal ? ' on' : '')}
            onClick={() => setShowInternal((v) => !v)}
            title="Dev/Test, Demo and Lushin are internal testing tenants, hidden by default."
          >
            Show internal<span className="c">{internalCount}</span>
          </button>
        )}
        <span className="grow" />
        <span className="freshness">{visible.length} shown</span>
      </div>

      <div className="tbl">
        <table>
          <thead>
            <tr>
              <th style={{ width: '18%' }} className={thCls('name')} onClick={() => setSort('name')}>
                Client <span className="sort-arr">{arrow('name')}</span>
              </th>
              {COLUMNS.map((c) => (
                <th key={c.key} className={thCls(c.key)} onClick={() => setSort(c.key)}>
                  {c.label} <span className="sort-arr">{arrow(c.key)}</span>
                </th>
              ))}
              <th>Plan</th>
              <th>Seats</th>
              <th className={thCls('riskScore')} onClick={() => setSort('riskScore')}>
                Risk <span className="sort-arr">{arrow('riskScore')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r: ClientRow) => (
              <tr
                key={r.tenantId}
                className={'clickable' + (r.isInternal ? ' internal-row' : '')}
                onClick={() => navigate(clientPath(r.tenantId, range))}
              >
                <td>
                  <ClientCell name={r.displayName} code={codeOf(r.tenantId)} sub={r.tenantId} />
                </td>
                {COLUMNS.map((c) => (
                  <td key={c.key}>
                    <MetricCell row={r} col={c} />
                  </td>
                ))}
                <td>
                  <StatusPill status={r.subscriptionStatus} />
                </td>
                <td>
                  {r.trialAccessEnabled ? (
                    <span className="chip" title="Trial access — seats are not capped">
                      Unlimited
                    </span>
                  ) : (
                    <span className="mono" style={{ color: 'var(--ink-3)' }}>
                      {r.seatsUsed}/{r.seatsTotal}
                    </span>
                  )}
                </td>
                <td>
                  {r.isAtRisk ? (
                    <FlagsCell flags={r.flags} />
                  ) : (
                    <span className="chip pos">OK</span>
                  )}
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={TOTAL_COLS}>
                  <EmptyState>
                    No clients match. <b>Adjust your search</b> or clear the at-risk filter.
                  </EmptyState>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
